/**
 *
 * ResourceItemInfoButton
 *
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Grid from '@material-ui/core/Grid';
import MixPanel from 'utils/mixpanelService';
import ClassNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { withStyles } from '@material-ui/core/styles';
import RemindMeDialog from 'components/RemindMeLaterDialog';
import {
  makeSelectGetRemindMeLaterResourceResult,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import {
  getRemindMeLaterResource,
  getRemindMeLaterResourceResult,
  setLeavingModal,
} from 'containers/Main/actions';
import { isEmbedded } from 'utils/embedded';
import { isSafari } from 'utils/stringUtils';
import NewTabIcon from './images/new-tab.svg';

const styles = () => ({
  linkBtn: {
    minWidth: 32,
    padding: 0,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& img': {
      width: props => props.isSocial && 36,
      height: props => (props.isSocial ? 36 : 49),
    },
  },
  socialBtn: {
    borderRadius: 18,
  },
  borderBtn: {
    border: '1px solid #979797',
    minWidth: 102,
  },
  websiteBtn: {
    width: 160,
    height: 49,
    borderRadius: 5,
    padding: 2,
    margin: 0,
    fontSize: 12,
    fontFamily: 'MadaMedium',
    textTransform: 'none',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  banner211Btn: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    borderRadius: '0.5rem',
    padding: '0.625rem 1rem',
  },
  icon: {
    marginLeft: 23,
    marginRight: 15,
  },
});

const ResourceItemInfoButton = props => {
  const {
    classes,
    children,
    type,
    label,
    withBorder,
    trackMessage,
    url,
    name,
    resourceType,
    exitPage,
    subtype,
    blog,
    data = {},
    organization,
    showLeavingDialog = false,
    isSocial,
    banner211Link,
  } = props;

  const isEmbed = isEmbedded();

  const [remind, setRemind] = useState(false);
  const [resourceItemPageSiteCopy, remindMeSiteCopy] = useSiteCopySelector([
    'resource-item-page',
    'remind-me',
  ]);
  const exitLinkMapping = _get(
    remindMeSiteCopy,
    'pageCopy.exitLinkMapping',
    {},
  );
  const thanksLinkMapping = _get(
    remindMeSiteCopy,
    'pageCopy.thanksLinkMapping',
    {},
  );
  const dispatch = useDispatch();
  const remindMeLaterResource = useSelector(
    makeSelectGetRemindMeLaterResourceResult(),
  );
  const clientDetails = useSelector(makeSelectClientDetails());
  const allowSignIn = _get(clientDetails, 'allowSignIn', true);
  const redirect = () => {
    const trackProperties = {
      name,
      type: resourceType,
      exitPage,
      subtype,
      blog,
      entryId: _get(data, 'sys.id'),
    };
    MixPanel.track(trackMessage, trackProperties);
    window.open(url, '_blank', !isSafari() ? 'noopener' : '');
  };

  useEffect(() => {
    dispatch(getRemindMeLaterResourceResult({}));
  }, []);

  useEffect(() => {
    dispatch(getRemindMeLaterResource({ resourceId: _get(data, 'sys.id') }));
  }, [resourceType]);

  const handleRemindMeAction = () => {
    if (!allowSignIn) {
      redirect();
      return;
    }
    if (!_get(remindMeLaterResource, 'added', false) && !_isEmpty(data)) {
      setRemind(true);
    } else {
      redirect();
    }
  };
  const organizationName =
    organization &&
    _get(organization.find(x => _get(x, 'fields.name')), 'fields.name');
  const handleLeavingDialogAction = () => {
    dispatch(
      setLeavingModal({
        visible: true,
        title: name,
        url,
        organization: organizationName,
        resourceType: props.resourceType,
        leavingModal: showLeavingDialog,
      }),
    );
  };

  const handleBtnClick = isEmbed
    ? redirect
    : showLeavingDialog
    ? handleLeavingDialogAction
    : handleRemindMeAction;

  if (type === 'website')
    return (
      <>
        {remind && (
          <RemindMeDialog
            data={data}
            toggle={() => setRemind(false)}
            link={exitLinkMapping[exitPage] || exitPage}
            thanksLink={thanksLinkMapping[exitPage] || exitPage}
            redirect={redirect}
          />
        )}
        <Grid item className={classes.linkBtnWrapper}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleBtnClick}
            disableFocusRipple
            disableElevation
            className={ClassNames({
              [classes.websiteBtn]: _isEmpty(banner211Link),
              [classes.banner211Btn]: !_isEmpty(banner211Link),
            })}
          >
            {_isEmpty(banner211Link) && (
              <img src={NewTabIcon} alt="New Tab" className={classes.icon} />
            )}
            {label ||
              _get(resourceItemPageSiteCopy, 'pageCopy.officialSiteLabel')}
          </Button>
        </Grid>
      </>
    );
  return (
    <>
      {remind && (
        <RemindMeDialog
          data={data}
          toggle={() => setRemind(false)}
          link={exitLinkMapping[exitPage] || exitPage}
          thanksLink={thanksLinkMapping[exitPage] || exitPage}
          redirect={redirect}
        />
      )}
      <Grid item className={classes.linkBtnWrapper}>
        <Button
          disableFocusRipple
          onClick={handleBtnClick}
          className={ClassNames(
            classes.linkBtn,
            isSocial && classes.socialBtn,
            withBorder && classes.borderBtn,
          )}
        >
          {children}
        </Button>
      </Grid>
    </>
  );
};

ResourceItemInfoButton.propTypes = {
  classes: PropTypes.object,
  children: PropTypes.node,
  type: PropTypes.string,
  url: PropTypes.string,
  trackMessage: PropTypes.string,
  resourceType: PropTypes.string,
  name: PropTypes.string,
  exitPage: PropTypes.string,
  label: PropTypes.string,
  subtype: PropTypes.string,
  withBorder: PropTypes.bool,
  showLeavingDialog: PropTypes.bool,
  blog: PropTypes.bool,
};

export default withStyles(styles)(ResourceItemInfoButton);
